import type { ComponentProps } from 'react';
import { type Stack } from 'expo-router';
import { theme, TYPE_CONFIG } from '@sg/garnish';

// ─── Base ─────────────────────────────────────────────────────────────────────

/**
 * Base screen options shared by all Stack navigators.
 *
 * NOTE: `headerLeft` is set to prevent the default `HeaderBackButton` from
 * rendering on web. `@react-navigation/elements` v2's `HeaderBackButton`
 * uses `useLocale` which doesn't exist in `@react-navigation/native` v6.
 * Screens that need custom header-left buttons set them via `setOptions`.
 */
export const DEFAULT_SCREEN_OPTIONS: ScreenOptions = {
  headerShown: false,
  headerLeft: () => null,
  contentStyle: { backgroundColor: theme.colors.APP_BACKGROUND },
};

// ─── Modal ────────────────────────────────────────────────────────────────────

/**
 * Screen options for modal Stack navigators.
 * Used on `XS` breakpoint where modals are present as full-screen cards.
 */
export const MODAL_SCREEN_OPTIONS: ScreenOptions = {
  ...DEFAULT_SCREEN_OPTIONS,
  presentation: 'modal',
};

/**
 * Screen options for transparent modal Stack navigators.
 * Used on `SM`+ breakpoints where modals overlay on top of existing content.
 */
export const TRANSPARENT_MODAL_SCREEN_OPTIONS: ScreenOptions = {
  ...DEFAULT_SCREEN_OPTIONS,
  presentation: 'transparentModal',
  contentStyle: { backgroundColor: 'transparent' },
};

// ─── Header ───────────────────────────────────────────────────────────────────

/**
 * Screen options for Stack navigators that show native headers.
 */
export const HEADER_SCREEN_OPTIONS: ScreenOptions = {
  ...DEFAULT_SCREEN_OPTIONS,
  headerShadowVisible: false,
  headerTintColor: theme.colors.GREEN_1,
  headerStyle: { backgroundColor: theme.colors.APP_BACKGROUND },
  headerTitleStyle: TYPE_CONFIG.BODY['18'] as ScreenOptions['headerTitleStyle'],
  headerTitleAlign: 'center',
  headerBackVisible: false,
  headerTitle: '',
};

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Screen options type derived from expo-router's `Stack` component.
 * This avoids version mismatches between the root `@react-navigation/native-stack`
 * (v6) and expo-router's bundled copy (v7).
 */
type ScreenOptions = Extract<
  NonNullable<ComponentProps<typeof Stack>['screenOptions']>,
  Record<string, unknown>
>;
